import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'
import { Printer, FileText, Loader2, PanelLeftClose, PanelLeftOpen, AlertTriangle } from 'lucide-react'
import { quizScriptApi } from '@/api/quizScript'
import type { QuizScript } from '@quebon/shared-learning'
import { QuizRenderer } from '@quebon/shared-learning'
import {
  type PrintSettings,
  type PageInfo,
  LAYOUT,
  STEP4_LAYOUT,
  DEFAULT_PAGE_INFO,
  DEFAULT_SETTINGS,
  DEFAULT_PRESET_IDS,
  PAPER_SIZES,
  QUIZZES_PER_STEP,
  BLEED_SIZE_MM,
  mmToPx,
  getPageLayout,
  toQuizData,
  PageHeader,
  StepHeader,
  PageFooter,
  ChapterNumber,
  TitleQueture,
  TitleStepSvg,
  PrintSettingsPanel,
  PresetEditor,
  PrintQuizItem,
  Step4Section,
  useQuizMeasurement,
  usePageSplitter,
  useLowResDetection,
} from '@quebon/shared-learning'

export default function PrintSamplePage() {
  const { t } = useTranslation('admin')
  const [settings, setSettings] = useState<PrintSettings>({ ...DEFAULT_SETTINGS })
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [presetIds, setPresetIds] = useState<string[]>([...DEFAULT_PRESET_IDS])
  const [quizScripts, setQuizScripts] = useState<QuizScript[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const printRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)

  const fetchQuizzes = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return
    setIsLoading(true)
    setError(null)
    try {
      const data = await quizScriptApi.getQuizzes(ids, false, true)
      // Reorder to match requested ids order (API may return in arbitrary order)
      const idToScript = new Map(data.map((s) => [String(s.quizId), s]))
      const ordered = ids.map((id) => idToScript.get(id)).filter(Boolean) as QuizScript[]
      setQuizScripts(ordered)
    } catch (err) {
      console.error('Failed to fetch quizzes:', err)
      setError(t('print.fetchError'))
      setQuizScripts([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchQuizzes(presetIds)
  }, [presetIds, fetchQuizzes])

  const handleSettingChange = <K extends keyof PrintSettings>(key: K, value: PrintSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handlePageInfoChange = <K extends keyof PageInfo>(key: K, value: PageInfo[K]) => {
    setSettings((prev) => ({
      ...prev,
      pageInfo: { ...prev.pageInfo, [key]: value },
    }))
  }

  const handleSettingReset = () => setSettings({ ...DEFAULT_SETTINGS, pageInfo: { ...DEFAULT_PAGE_INFO } })
  const handlePresetApply = (ids: string[]) => setPresetIds(ids)

  // Paper dimensions
  const paper = PAPER_SIZES[settings.paperSize]
  const isLandscape = settings.orientation === 'landscape'
  const pageW = isLandscape ? paper.height : paper.width
  const pageH = isLandscape ? paper.width : paper.height
  const pageWidthPx = mmToPx(pageW)
  const pageHeightPx = mmToPx(pageH)

  const { pageInfo } = settings

  // Split quizzes into steps
  const steps: QuizScript[][] = []
  for (let i = 0; i < quizScripts.length; i += QUIZZES_PER_STEP) {
    steps.push(quizScripts.slice(i, i + QUIZZES_PER_STEP))
  }

  // Custom hooks
  const { measuredHeights, isMeasuring, remeasure } = useQuizMeasurement(
    measureRef,
    quizScripts,
    [settings.paperSize, settings.orientation, settings.columns, pageWidthPx, settings.compact]
  )
  const stepPages = usePageSplitter(steps, measuredHeights, pageHeightPx)
  const { footerTop } = getPageLayout(pageHeightPx)

  // Step 4 placement: check if it fits on the last step3 page
  const step4FitsInline = useMemo(() => {
    if (stepPages.length === 0) return false
    const lastPage = stepPages[stepPages.length - 1]

    // Calculate used height on last page
    let usedHeight = 0

    if (lastPage.isFirstOfStep) {
      if (lastPage.stepIdx === 0) {
        usedHeight += LAYOUT.header.totalHeight + LAYOUT.step1FirstPaddingTop + 56 + LAYOUT.quizTopPadding.afterStepTitle
      } else {
        usedHeight += (LAYOUT.step2.barHeight + LAYOUT.step2.curveHeight) + LAYOUT.quizTopPadding.step23FirstPage
      }
    } else {
      if (lastPage.stepIdx === 0) {
        usedHeight += LAYOUT.header.barHeight + LAYOUT.quizTopPadding.continuationGap
      } else {
        usedHeight += LAYOUT.step2.barHeight + LAYOUT.quizTopPadding.continuationGap
      }
    }

    const globalOffset = lastPage.stepIdx * QUIZZES_PER_STEP
    lastPage.quizzes.forEach((_, i) => {
      const quizIdx = globalOffset + (lastPage.quizStartNum - 1) + i
      usedHeight += measuredHeights[quizIdx] || 400
    })

    const footerSpace = 72 + LAYOUT.footer.bottomGap
    const availableSpace = pageHeightPx - usedHeight - footerSpace

    return availableSpace >= STEP4_LAYOUT.inlineHeight
  }, [stepPages, measuredHeights, pageHeightPx])

  // Gutter (binding margin)
  const gutterPx = settings.gutterEnabled ? mmToPx(settings.gutterSize) : 0

  // Bleed (도련) — always 3mm when enabled
  const bleedPx = settings.bleedEnabled ? mmToPx(BLEED_SIZE_MM) : 0
  const totalPageWidthPx = pageWidthPx + bleedPx * 2
  const totalPageHeightPx = pageHeightPx + bleedPx * 2
  const totalPageWmm = pageW + (settings.bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
  const totalPageHmm = pageH + (settings.bleedEnabled ? BLEED_SIZE_MM * 2 : 0)

  // Low-res image detection
  const lowResImages = useLowResDetection(printRef, quizScripts.length, isMeasuring)

  const startPageNum = 2

  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: ${totalPageWmm}mm ${totalPageHmm}mm;
        margin: 0;
      }
      @media print {
        html, body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .print-page {
          page-break-after: always !important;
          break-after: page !important;
          position: relative !important;
        }
        .print-page:last-child {
          page-break-after: auto !important;
          break-after: auto !important;
        }
        .break-inside-avoid {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .print-quiz-wrapper {
          display: flex;
          justify-content: center;
        }
        .print-quiz-wrapper button,
        .print-quiz-wrapper input {
          pointer-events: none;
        }
      }
    `,
  })

  return (
    <div className="flex gap-6 h-full">
      {/* Left: Toggle + Settings + Preset Editor */}
      <div className="flex flex-col gap-2 shrink-0 print:hidden sticky top-0 self-start">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors self-start"
          title={sidebarOpen ? t('print.hideSidebar') : t('print.showSidebar')}
        >
          {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
          {!sidebarOpen && <span className="text-xs font-medium">{t('print.settings')}</span>}
        </button>

        {sidebarOpen && (
          <div className="space-y-4">
            <PrintSettingsPanel
              settings={settings}
              onChange={handleSettingChange}
              onPageInfoChange={handlePageInfoChange}
              onReset={handleSettingReset}
            />
            <PresetEditor quizIds={presetIds} onApply={handlePresetApply} isLoading={isLoading} />
            <button
              onClick={() => reactToPrintFn()}
              disabled={quizScripts.length === 0 || isMeasuring}
              className="w-64 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isMeasuring ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t('print.measuring')}
                </>
              ) : (
                <>
                  <Printer size={16} />
                  {t('print.printButton')}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Right: A4 Preview */}
      <div className="flex overflow-auto flex-1 justify-center pb-8">
        <div className="flex flex-col gap-4 items-center">
          {/* Page info */}
          <div className="flex gap-2 items-center text-xs text-gray-500 print:hidden">
            <FileText size={14} />
            <span>
              {settings.paperSize} {settings.orientation === 'portrait' ? t('print.paper.portrait') : t('print.paper.landscape')}
              {quizScripts.length > 0 && ` · ${t('print.questionCount', { count: quizScripts.length })}`}
            </span>
          </div>

          {/* Low-res image warning */}
          {lowResImages.length > 0 && (
            <div className="flex items-start gap-2 p-3 text-xs text-amber-700 bg-amber-50 rounded-lg border border-amber-200 print:hidden">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>
                <span className="font-medium">저해상도 이미지 {lowResImages.length}개 감지</span>
                <span className="text-amber-600 ml-1">(300DPI 미만, 인쇄 품질 저하 가능)</span>
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex gap-2 items-center py-12 text-gray-500">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">{t('print.loading')}</span>
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="p-4 max-w-md text-sm text-red-700 bg-red-50 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Hidden measurement container */}
          {!isLoading && quizScripts.length > 0 && (
            <div
              ref={measureRef}
              aria-hidden
              style={{
                position: 'absolute',
                left: -9999,
                top: 0,
                width: `${pageWidthPx - LAYOUT.pageSidePadding * 2 - gutterPx}px`,
                visibility: 'hidden',
                lineHeight: 1.6,
              }}
            >
              {quizScripts.map((script, i) => (
                <div key={script.quizId ?? i} data-quiz-measure style={{ paddingBottom: `${LAYOUT.questionSpacing}px` }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <QuizRenderer
                      quizType={toQuizData(script).type}
                      quizId={String(script.quizId ?? '')}
                      quiz={toQuizData(script)}
                      mode="print"
                      number={i + 1}
                      hideSubmitButton
                      disableSound
                      compact={settings.compact}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* A4 Paper - Checkup Book Design */}
          {!isLoading && quizScripts.length > 0 && (
            <div
              ref={printRef}
              className="bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0"
              style={{
                width: `${totalPageWidthPx}px`,
                lineHeight: 1.6,
              }}
            >
              <style>{`
                .print-page { overflow: hidden; box-sizing: border-box; height: ${totalPageHeightPx}px; }
              `}</style>

              {/* Page 1 (table of contents, odd — odd pages get right gutter) */}
              <div className="print-page relative" style={{ width: `${totalPageWidthPx}px` }}>

                <div className="relative" style={{ marginRight: gutterPx, height: totalPageHeightPx }}>
                  <div style={{ padding: `${40 + bleedPx}px ${60 + bleedPx}px` }}>
                    <h2
                      className="font-dunggeunmiso"
                      style={{ fontSize: 32, color: '#2B3B7A', fontWeight: 700 }}
                    >
                      목차
                    </h2>
                  </div>
                  {settings.showPageNumber && (
                    <PageFooter pageNumber={1} isEven={false} bookLabel={pageInfo.bookLabel} footerTop={footerTop} bleedPx={bleedPx} />
                  )}
                </div>
              </div>

              {/* Step pages */}
              {stepPages.map((sp, pageIdx) => {
                const pageNumber = startPageNum + pageIdx
                const isEven = pageNumber % 2 === 0
                const isStep1 = sp.stepIdx === 0
                const isStep1FirstPage = isStep1 && sp.isFirstOfStep

                return (
                  <div
                    key={`page-${pageIdx}`}
                    className="print-page relative"
                    style={{ width: `${totalPageWidthPx}px` }}
                  >
    
                    <div className="relative" style={{
                      marginLeft: isEven && gutterPx > 0 ? gutterPx : 0,
                      marginRight: !isEven && gutterPx > 0 ? gutterPx : 0,
                      height: totalPageHeightPx,
                    }}>
                      {/* Header */}
                      {isStep1FirstPage ? (
                        <>
                          <PageHeader isEven={isEven} bleedPx={bleedPx} />
                          <ChapterNumber number={pageInfo.chapterNumber} bleedPx={bleedPx} />
                          <TitleQueture
                            grade={pageInfo.grade}
                            unitLabel={pageInfo.unitLabel}
                            unitName={pageInfo.unitName}
                            quetureTitle={pageInfo.quetureTitle}
                            bleedPx={bleedPx}
                          />
                        </>
                      ) : isStep1 ? (
                        <PageHeader isEven={isEven} bleedPx={bleedPx} />
                      ) : (
                        <div className="relative">
                          <StepHeader stepIndex={sp.stepIdx} isEven={isEven} bleedPx={bleedPx} />
                          {sp.isFirstOfStep && <TitleStepSvg stepIndex={sp.stepIdx} isAbsolute />}
                        </div>
                      )}

                      {/* Content area */}
                      <div style={{
                        paddingTop: isStep1FirstPage ? LAYOUT.step1FirstPaddingTop + bleedPx : 0,
                        position: 'relative',
                        zIndex: 1,
                      }}>
                        {isStep1FirstPage && <TitleStepSvg stepIndex={0} />}

                        {(() => {
                          // Compute quiz container spacing
                          // Goal: 64px visual gap from colored bar for continuation pages (curve is white)
                          let quizPadTop: number
                          let quizMarginTop = 0

                          if (isStep1FirstPage) {
                            quizPadTop = LAYOUT.quizTopPadding.afterStepTitle // 40px after inline SVG
                          } else if (sp.isFirstOfStep) {
                            quizPadTop = LAYOUT.quizTopPadding.step23FirstPage // 32px (visual 40px from abs SVG)
                          } else if (isStep1) {
                            // Step1 cont: curve=80px (white). Want 64px total from bar.
                            // Pull content up into curve: -(80 - (64-0)) = -16
                            quizPadTop = 0
                            quizMarginTop = -(LAYOUT.header.curveHeight - LAYOUT.quizTopPadding.continuationGap)
                          } else {
                            // Step2/3 cont: curve=40px (white). Remaining = 64-40 = 24px
                            quizPadTop = LAYOUT.quizTopPadding.continuationGap - LAYOUT.step2.curveHeight
                          }

                          return (
                        <div
                          style={{
                            paddingTop: quizPadTop,
                            marginTop: quizMarginTop,
                            paddingLeft: LAYOUT.pageSidePadding + bleedPx,
                            paddingRight: LAYOUT.pageSidePadding + bleedPx,
                            columnCount: settings.columns,
                            columnGap: `${LAYOUT.pageSidePadding}px`,
                            paddingBottom: 0,
                          }}
                        >
                          {sp.quizzes.map((script, i) => (
                            <PrintQuizItem
                              key={script.quizId ?? i}
                              script={script}
                              number={sp.quizStartNum + i}
                              stepIdx={sp.stepIdx}
                              compact={settings.compact}
                            />
                          ))}
                        </div>
                          )
                        })()}
                      </div>

                      {/* Step 4 inline (pinned to bottom of usable area, 20px above footer) */}
                      {pageIdx === stepPages.length - 1 && step4FitsInline && settings.step4Questions.length > 0 && (
                        <div style={{ position: 'absolute', bottom: totalPageHeightPx - (footerTop + bleedPx) + 20, left: bleedPx, right: bleedPx }}>
                          <Step4Section questions={settings.step4Questions} />
                        </div>
                      )}

                      {/* Footer */}
                      {settings.showPageNumber && (
                        <PageFooter
                          pageNumber={pageNumber}
                          isEven={isEven}
                          bookLabel={pageInfo.bookLabel}
                          footerTop={footerTop}
                          bleedPx={bleedPx}
                        />
                      )}
                    </div>
                  </div>
                )
              })}

              {/* Step 4 on new page (when it doesn't fit inline on last step page) */}
              {!step4FitsInline && settings.step4Questions.length > 0 && stepPages.length > 0 && (() => {
                const step4PageNumber = startPageNum + stepPages.length
                const step4IsEven = step4PageNumber % 2 === 0
                return (
                  <div
                    key="step4-page"
                    className="print-page relative"
                    style={{ width: `${totalPageWidthPx}px` }}
                  >
    
                    <div className="relative" style={{
                      marginLeft: step4IsEven && gutterPx > 0 ? gutterPx : 0,
                      marginRight: !step4IsEven && gutterPx > 0 ? gutterPx : 0,
                      height: totalPageHeightPx,
                    }}>
                      <div className="relative">
                        <StepHeader stepIndex={3} isEven={step4IsEven} bleedPx={bleedPx} />
                        <TitleStepSvg stepIndex={3} isAbsolute />
                      </div>
                      <div style={{ paddingTop: LAYOUT.quizTopPadding.step23FirstPage, paddingLeft: bleedPx, paddingRight: bleedPx }}>
                        <Step4Section questions={settings.step4Questions} showTitle={false} />
                      </div>
                      {settings.showPageNumber && (
                        <PageFooter
                          pageNumber={step4PageNumber}
                          isEven={step4IsEven}
                          bookLabel={pageInfo.bookLabel}
                          footerTop={footerTop}
                          bleedPx={bleedPx}
                        />
                      )}
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && quizScripts.length === 0 && (
            <div className="py-12 text-sm text-center text-gray-400">
              {t('print.empty')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
